import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  deactivateOrganization,
  getCategoryData,
  getLocationData,
  renderEmployees,
  renderOrganizations,
} from "Config/API/get";
import {
  activeInactiveEmployee,
  makeOrgAdmin,
  updateEmail,
} from "Config/API/action";
import { updateUser } from "Config/API/update";
import "./OrganizationList.css";
import { Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import blank from "Media/blank-profile-picture.png";
import { toast } from "react-toastify";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockResetIcon from "@mui/icons-material/LockReset";
import ArchiveIcon from "@mui/icons-material/Archive";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EmailIcon from "@mui/icons-material/Email";
import { resetPassword } from "Config/API/authentication";
import { getStripeConnectedIdFromAdminProfile } from "Helper/stripeConnectedAccount";

const DEBOUNCE_MS = 350;

function useOutsideAlerter(ref) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        ref.current.classList.add("hide-dropdown");
      } else if (ref.current) {
        ref.current.classList.remove("hide-dropdown");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ref]);
}

const OrganizationList = () => {
  const token = localStorage.getItem("token");
  const [orgList, setOrgList] = useState("");
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [orgLoading, setOrgLoading] = useState(false);
  const orgSearchTimeoutRef = useRef(null);

  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedOrgDetails, setSelectedOrgDetails] = useState(null);
  const [selectedOrgAdmin, setSelectedOrgAdmin] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [resetPasswordNotify, setResetPasswordNotify] = useState(true);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  const [userList, setUserList] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userLoading, setUserLoading] = useState(false);

  const [currentOrgAdmin, setCurrentOrgAdmin] = useState(null);
  const [makeOrgAdminOpen, setMakeOrgAdminOpen] = useState(false);
  const [locationList, setLocationList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [adminLocationId, setAdminLocationId] = useState("");
  const [adminCategoryId, setAdminCategoryId] = useState("");
  const [adminDesignation, setAdminDesignation] = useState("");
  const [makeOrgAdminLoading, setMakeOrgAdminLoading] = useState(false);

  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  const [updateEmailOpen, setUpdateEmailOpen] = useState(false);
  const [updateEmailValue, setUpdateEmailValue] = useState("");
  const [updateEmailLoading, setUpdateEmailLoading] = useState(false);

  const wrapperRef = useRef(null);
  const userRef = useRef(null);
  useOutsideAlerter(wrapperRef);
  useOutsideAlerter(userRef);

  const fetchOrganizations = useCallback(
    (searchKeywords = "") => {
      setOrgLoading(true);
      Promise.resolve(renderOrganizations(token, searchKeywords))
        .then((res) => {
          if (res?.status) {
            setOrgList(res?.listOfOrganisation ?? "");
          } else {
            toast(res?.message, { type: "error" });
            setOrgList("");
          }
        })
        .catch(() => {
          setOrgList("");
        })
        .finally(() => setOrgLoading(false));
    },
    [token]
  );

  const handleOrgInputClick = useCallback(() => {
    fetchOrganizations(orgSearchQuery);
  }, [orgSearchQuery, fetchOrganizations]);

  const handleOrgInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setOrgSearchQuery(value);
      if (orgSearchTimeoutRef.current) clearTimeout(orgSearchTimeoutRef.current);
      orgSearchTimeoutRef.current = setTimeout(() => {
        fetchOrganizations(value);
      }, DEBOUNCE_MS);
    },
    [fetchOrganizations]
  );

  const clearOrgSearch = useCallback(() => {
    setOrgSearchQuery("");
    fetchOrganizations("");
  }, [fetchOrganizations]);

  const handleSelectOrg = useCallback((org) => {
    console.log(`Selected organization:`, org);
    setSelectedOrg(org?.org_id ?? "");
    setSelectedOrgAdmin(org?.org_admin_id ?? null);
    setSelectedOrgDetails({
      id: org?.id,
      name: org?.organization_name || "No Name",
      logo: org?.logo || blank,
      fullOrg: org,
    });
    setOrgList("");
    setOrgSearchQuery("");
    setSelectedUser(null);
    // Current org admin details come from the organization list item (same API response)
    const hasAdminDetails =
      org?.role === "admin" ||
      (typeof org?.role === "string" && org.role.toLowerCase() === "admin") ||
      org?.username != null ||
      org?.username !== "" ||
      org?.email != null ||
      org?.email !== "";
    if (hasAdminDetails) {
      setCurrentOrgAdmin({
        id: org?.admin_id ?? org?.user_id ?? null,
        username: org?.username ?? "",
        firstname: org?.firstname ?? "",
        lastname: org?.lastname ?? "",
        email: org?.email ?? "",
        image: org?.image ?? "",
        role: org?.role ?? "",
        category: org?.category ?? "",
        location: org?.location ?? "",
        designation: org?.designation ?? "",
        phone: org?.phone ?? "",
        office_phone: org?.office_phone ?? "",
        extension: org?.extension ?? "",
        scope: org?.scope ?? "",
        dob: org?.dob ?? "",
        org_id: org?.org_id ?? org?.id ?? "",
        stripe_connected_id: org?.stripe_connected_id ?? "",
      });
    } else {
      setCurrentOrgAdmin(null);
    }
  }, []);

  const handleDeselectOrg = useCallback(() => {
    setSelectedOrg("");
    setSelectedOrgAdmin("")
    setSelectedOrgDetails(null);
    setSelectedUser(null);
    setCurrentOrgAdmin(null);
    setUserList("");
    setUserSearchQuery("");
  }, []);

  // Derive current org admin from state or from selected org (list item has admin details). Must be before any callback that uses it.
  const effectiveCurrentOrgAdmin = useMemo(() => {
    if (currentOrgAdmin && (currentOrgAdmin.username || currentOrgAdmin.email || currentOrgAdmin.role))
      return currentOrgAdmin;
    const fullOrg = selectedOrgDetails?.fullOrg;
    if (!fullOrg) return null;
    const hasAdmin =
      fullOrg.username ||
      fullOrg.email ||
      fullOrg.role === "admin" ||
      (typeof fullOrg.role === "string" && fullOrg.role.toLowerCase() === "admin");
    if (!hasAdmin) return null;
    return {
      id: fullOrg?.admin_id ?? fullOrg?.user_id ?? null,
      username: fullOrg?.username ?? "",
      firstname: fullOrg?.firstname ?? "",
      lastname: fullOrg?.lastname ?? "",
      email: fullOrg?.email ?? "",
      image: fullOrg?.image ?? "",
      role: fullOrg?.role ?? "",
      category: fullOrg?.category ?? "",
      location: fullOrg?.location ?? "",
      designation: fullOrg?.designation ?? "",
      phone: fullOrg?.phone ?? "",
      office_phone: fullOrg?.office_phone ?? "",
      extension: fullOrg?.extension ?? "",
      scope: fullOrg?.scope ?? "",
      dob: fullOrg?.dob ?? "",
      org_id: fullOrg?.org_id ?? fullOrg?.id ?? "",
      stripe_connected_id: fullOrg?.stripe_connected_id ?? "",
    };
  }, [currentOrgAdmin, selectedOrgDetails?.fullOrg]);

  const handleSelectUser = useCallback((user) => {
    setSelectedUser(user);
  }, []);

  const handleDeselectUser = useCallback(() => {
    setSelectedUser(null);
    setResetPasswordOpen(false);
    setResetPasswordValue("");
    setResetPasswordConfirm("");
  }, []);

  const handleResetPasswordOpen = useCallback(() => {
    setResetPasswordValue("");
    setResetPasswordConfirm("");
    setResetPasswordNotify(true);
    setShowResetPassword(false);
    setShowResetPasswordConfirm(false);
    setResetPasswordOpen(true);
  }, []);

  const handleResetPasswordClose = useCallback(() => {
    setResetPasswordOpen(false);
    setResetPasswordValue("");
    setResetPasswordConfirm("");
    setShowResetPassword(false);
    setShowResetPasswordConfirm(false);
  }, []);

  const handleResetPasswordSubmit = useCallback(() => {
    if (!selectedUser?.id) return;
    if (!resetPasswordValue.trim()) {
      toast("Enter new password", { type: "error" });
      return;
    }
    if (resetPasswordValue !== resetPasswordConfirm) {
      toast("Passwords do not match", { type: "error" });
      return;
    }
    setResetPasswordLoading(true);
    const reset_status = resetPassword(
      resetPasswordValue,
      resetPasswordConfirm,
      token,
      selectedUser.id,
      resetPasswordNotify
    );
    reset_status
      .then((res) => {
        if (res?.status) {
          toast(res?.message ?? "Password reset successfully", {
            type: "success",
          });
          handleResetPasswordClose();
        } else {
          toast(res?.message ?? "Failed to reset password", { type: "error" });
        }
      })
      .catch(() => toast("Failed to reset password", { type: "error" }))
      .finally(() => setResetPasswordLoading(false));
  }, [
    selectedUser?.id,
    resetPasswordValue,
    resetPasswordConfirm,
    resetPasswordNotify,
    token,
    handleResetPasswordClose,
  ]);

  const handleArchiveUserClick = useCallback(() => {
    if (!selectedUser?.id) return;
    setArchiveConfirmOpen(true);
  }, [selectedUser?.id]);

  const handleArchiveConfirmClose = useCallback(() => setArchiveConfirmOpen(false), []);

  const handleArchiveUserConfirm = useCallback(() => {
    if (!selectedUser?.id) return;
    const isCurrentlyActive = selectedUser?.status === "active";
    const status = isCurrentlyActive ? "inactive" : "active";
    setArchiveConfirmOpen(false);
    Promise.resolve(activeInactiveEmployee(status, selectedUser.id, token)).then(
      (res) => {
        if (res?.status) {
          toast(
            isCurrentlyActive
              ? res?.message ?? "User archived successfully"
              : res?.message ?? "User activated successfully",
            { type: "success" }
          );
          setSelectedUser(null);
          if (selectedOrg) {
            Promise.resolve(renderEmployees(token, selectedOrg)).then((r) => {
              if (r?.status) setUserList(r?.employeOfOrganization ?? "");
            });
          }
        } else {
          toast(res?.message ?? (isCurrentlyActive ? "Failed to archive user" : "Failed to activate user"), {
            type: "error",
          });
        }
      }
    );
  }, [selectedUser?.id, selectedUser?.status, selectedOrg, token]);

  const handleUpdateEmailOpen = useCallback(() => {
    if (!selectedUser?.id) return;
    setUpdateEmailValue(selectedUser?.email ?? "");
    setUpdateEmailOpen(true);
  }, [selectedUser?.id, selectedUser?.email]);

  const handleUpdateEmailClose = useCallback(() => {
    setUpdateEmailOpen(false);
    setUpdateEmailValue("");
  }, []);

  const handleUpdateEmailSubmit = useCallback(() => {
    if (!selectedUser?.id) return;
    const trimmed = updateEmailValue?.trim?.() ?? "";
    if (!trimmed) {
      toast("Enter a new login email", { type: "error" });
      return;
    }
    setUpdateEmailLoading(true);
    Promise.resolve(updateEmail(token, selectedUser.id, trimmed))
      .then((res) => {
        if (res?.status) {
          toast(res?.message ?? "Login email updated successfully", {
            type: "success",
          });
          setSelectedUser((prev) => (prev ? { ...prev, email: trimmed } : null));
          if (selectedOrg) {
            Promise.resolve(renderEmployees(token, selectedOrg)).then((r) => {
              if (r?.status) setUserList(r?.employeOfOrganization ?? "");
            });
          }
          handleUpdateEmailClose();
        } else {
          toast(res?.message ?? "Failed to update email", { type: "error" });
        }
      })
      .catch(() => toast("Failed to update email", { type: "error" }))
      .finally(() => setUpdateEmailLoading(false));
  }, [selectedUser?.id, selectedOrg, token, updateEmailValue, handleUpdateEmailClose]);

  const handleMakeOrgAdminClick = useCallback(() => {
    if (!selectedUser?.id) return;
    setMakeOrgAdminOpen(true);
    setAdminLocationId("");
    setAdminCategoryId("");
    setAdminDesignation(effectiveCurrentOrgAdmin?.designation ?? "");
    setCategoryList([]);
    if (selectedOrg) {
      Promise.resolve(getLocationData(token, selectedOrg)).then((res) => {
        setLocationList(res?.location ?? []);
      });
    }
  }, [selectedUser?.id, selectedOrg, token, effectiveCurrentOrgAdmin]);

  useEffect(() => {
    if (!makeOrgAdminOpen || !adminLocationId || !selectedOrg) {
      setCategoryList([]);
      return;
    }
    Promise.resolve(getCategoryData(token, selectedOrg, adminLocationId)).then(
      (res) => setCategoryList(res?.category ?? [])
    );
  }, [makeOrgAdminOpen, adminLocationId, selectedOrg, token]);

  const handleMakeOrgAdminClose = useCallback(() => {
    setMakeOrgAdminOpen(false);
    setAdminLocationId("");
    setAdminCategoryId("");
    setAdminDesignation("");
    setLocationList([]);
    setCategoryList([]);
  }, []);

  const handleMakeOrgAdminSubmit = useCallback(() => {
    if (!selectedUser?.id) return;
    const effectiveAdmin = effectiveCurrentOrgAdmin;
    if (effectiveAdmin) {
      if (!adminLocationId || !adminCategoryId) {
        toast(
          "Select location and category for the current admin before continuing.",
          { type: "warning" }
        );
        return;
      }
    }
    setMakeOrgAdminLoading(true);
    const oldAdminId =
      selectedOrgAdmin ?? selectedOrgDetails?.fullOrg?.org_admin_id ?? null;
    const adminId =
      effectiveAdmin?.id ??

      (Array.isArray(userList) && effectiveAdmin?.email
        ? (userList.find((u) => (u?.email || "").toLowerCase() === (effectiveAdmin?.email || "").toLowerCase())?.id)
        : null);
    const stripeConnectedId = getStripeConnectedIdFromAdminProfile({
      org_info: selectedOrgDetails?.fullOrg,
      stripe_connected_id: effectiveAdmin?.stripe_connected_id,
    });
    const doMakeAdmin = () =>
      Promise.resolve(
        makeOrgAdmin(
          token,
          oldAdminId,
          selectedUser.id,
          adminLocationId || undefined,
          adminCategoryId || undefined,
          adminDesignation || undefined,
          selectedUser?.org_id,
          stripeConnectedId
        )
      ).then((res) => {
        if (res?.status) {
          toast(res?.message ?? "User set as organization admin", {
            type: "success",
          });
          handleMakeOrgAdminClose();
          setSelectedUser(null);
          if (selectedOrg) {
            Promise.resolve(renderEmployees(token, selectedOrg)).then((r) => {
              if (r?.status) {
                setUserList(r?.employeOfOrganization ?? "");
                const adm = (r?.employeOfOrganization ?? []).find(
                  (u) =>
                    u?.is_org_admin ||
                    u?.role === "org_admin" ||
                    u?.role === "admin"
                );
                setCurrentOrgAdmin(adm ?? null);
              }
            });
          }
        } else {
          toast(res?.message ?? "Failed to make org admin", { type: "error" });
        }
        setMakeOrgAdminLoading(false);
      });
    if (effectiveAdmin && adminId && adminLocationId && adminCategoryId) {
      const admin = effectiveAdmin;
      Promise.resolve(
        updateUser(
          adminId,
          admin.username ?? "",
          admin.firstname ?? "",
          admin.lastname ?? "",
          admin.email ?? "",
          admin.phone ?? "",
          admin.office_phone ?? "",
          admin.extension ?? "",
          (adminDesignation || admin.designation) ?? "",
          "",
          admin.image ?? "",
          admin.org_id ?? selectedOrg,
          adminCategoryId,
          admin.scope ?? "",
          admin.dob ?? "",
          adminLocationId,
          token
        )
      ).then((res) => {
        if (res?.status) {
          doMakeAdmin();
        } else {
          toast(res?.message ?? "Failed to update current admin", {
            type: "error",
          });
          setMakeOrgAdminLoading(false);
        }
      });
    } else {
      doMakeAdmin();
    }
  }, [
    selectedUser?.id,
    selectedOrg,
    selectedOrgAdmin,
    selectedOrgDetails?.fullOrg,
    token,
    effectiveCurrentOrgAdmin,
    userList,
    adminLocationId,
    adminCategoryId,
    adminDesignation,
    handleMakeOrgAdminClose,
  ]);

  useEffect(() => {
    if (!selectedOrg?.toString()) {
      setUserList("");
      setUserLoading(false);
      return;
    }
    setUserLoading(true);
    Promise.resolve(renderEmployees(token, selectedOrg))
      .then((res) => {
        if (res?.status) {
          const employees = res?.employeOfOrganization ?? "";
          setUserList(employees);
          // If we have currentOrgAdmin from org list but no admin id, get id from employee list
          setCurrentOrgAdmin((prev) => {
            if (!prev) {
              const fromList = Array.isArray(employees)
                ? employees.find(
                    (u) =>
                      u?.is_org_admin ||
                      u?.role === "org_admin" ||
                      u?.role === "admin"
                  )
                : null;
              return fromList ?? null;
            }
            const match = Array.isArray(employees)
              ? employees.find(
                  (u) =>
                    u?.email === prev.email ||
                    u?.role === "admin" ||
                    u?.is_org_admin ||
                    u?.role === "org_admin"
                )
              : null;
            if (match?.id)
              return {
                ...prev,
                id: match.id,
                stripe_connected_id:
                  match.stripe_connected_id ?? prev.stripe_connected_id,
              };
            return prev;
          });
        } else {
          toast(res?.message, { type: "error" });
          setUserList("");
        }
      })
      .catch(() => setUserList(""))
      .finally(() => setUserLoading(false));
  }, [token, selectedOrg]);

  const handleUserInputClick = useCallback(() => {
    if (selectedOrg?.toString() === "") {
      toast("Select an Organization First!", { type: "info" });
    } else if (!userList?.length && !userLoading) {
      setUserLoading(true);
      Promise.resolve(renderEmployees(token, selectedOrg))
        .then((res) => {
          if (res?.status) setUserList(res?.employeOfOrganization ?? "");
        })
        .catch(() => setUserList(""))
        .finally(() => setUserLoading(false));
    }
  }, [token, selectedOrg, userList?.length, userLoading]);

  const clearUserSearch = useCallback(() => setUserSearchQuery(""), []);

  const filteredUserList = useMemo(() => {
    if (!userList || !Array.isArray(userList) || userList.message)
      return userList;
    if (!userSearchQuery.trim()) return userList;
    const q = userSearchQuery.trim().toLowerCase();
    return userList.filter((user) => {
      const name = (user?.username ?? "").toLowerCase();
      const email = (user?.email ?? "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [userList, userSearchQuery]);

  const displayUserList = userSearchQuery.trim() ? filteredUserList : userList;
  const hasOrgResults = Array.isArray(orgList) && orgList.length > 0;
  const orgNoResultsMessage =
    orgList && typeof orgList === "object" && typeof orgList.message === "string"
      ? orgList.message
      : null;
  const hasUserResults =
    Array.isArray(displayUserList) && displayUserList.length > 0;
  const userNoResultsMessage = displayUserList?.message;
  const userListIsArray = Array.isArray(userList) && !userList.message;
  const userFilterEmpty =
    userListIsArray &&
    userSearchQuery.trim() &&
    (!displayUserList || displayUserList.length === 0);

  return (
    <div className="organization-list-page">
      <div className="organization-wrapper">
        <div className="internal-head">List of Organization: </div>

        {selectedOrgDetails && (
          <div className="selected-org-card">
            <div className="selected-org-card-content">
              <img
                src={selectedOrgDetails.logo}
                alt=""
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = blank;
                }}
              />
              <div className="selected-org-card-info">
                <span className="selected-org-card-label">Selected as org</span>
                <span className="selected-org-card-name">
                  {selectedOrgDetails.name}
                </span>
              </div>
            </div>
            <Button
              type="button"
              variant="outlined"
              color="error"
              size="small"
              className="selected-org-deselect-btn"
              onClick={handleDeselectOrg}
              startIcon={<CloseIcon />}
            >
              Clear selection
            </Button>
          </div>
        )}

        <div className="internal-wrapper" ref={wrapperRef}>
          <div className={`org-search-wrap ${orgSearchQuery ? "has-clear" : ""}`}>
            <input
              type="text"
              placeholder="Search Organization..."
              value={orgSearchQuery}
              onChange={handleOrgInputChange}
              onClick={handleOrgInputClick}
              className={orgLoading ? "org-search-loading" : ""}
              autoComplete="off"
              name="org-search"
              aria-label="Search organization"
            />
            {orgSearchQuery && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={clearOrgSearch}
                aria-label="Clear search"
              >
                <CloseIcon fontSize="small" />
              </button>
            )}
            {orgLoading && (
              <span className="search-loading-text">Searching...</span>
            )}
          </div>

          <div className="organization-list">
            {orgLoading && !hasOrgResults ? (
              <div className="orgListAlert org-list-loading">
                {/* {orgSearchQuery.trim() ? "Searching..." : "Loading..."} */}
              </div>
            )  : (
              hasOrgResults &&
              orgList.map((curr, index) => (
                <div
                  className={`data-list ${curr?.id === selectedOrg ? "data-list-selected" : ""}`}
                  key={curr?.id ?? index}
                >
                  <div>
                    <img
                      src={curr?.logo || blank}
                      alt="Logo"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = blank;
                      }}
                    />
                    <span>
                      {curr?.organization_name
                        ? curr?.organization_name
                        : "No Name"}
                    </span>
                  </div>
                  <div className="button-container">
                    {curr?.id === selectedOrg ? (
                      <Button
                        type="button"
                        variant="contained"
                     
                        onClick={(e) => {
                          e.preventDefault();
                          handleDeselectOrg();
                        }}
                      >
                        Deselect
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="contained"
                        onClick={(e) => {
                          e.preventDefault();
                          handleSelectOrg(curr);
                          setOrgList("")
                        }}
                      >
                        Select
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outlined"
                      className="superadmin-delete"
                      onClick={(e) => {
                        e.preventDefault();
                        const status =
                          curr?.status === "active" ? "inactive" : "active";
                        deactivateOrganization(
                          token,
                          status,
                          curr?.id
                        ).then((res) => {
                          if (res?.status) {
                            toast(res?.message, { type: "success" });
                            fetchOrganizations(orgSearchQuery);
                          }
                        });
                      }}
                    >
                      {curr?.status === "active"
                        ? "Deactivate"
                        : "Activate"}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="user-wrapper">
        <div className="internal-head">List of Employees: </div>

        {selectedOrg && effectiveCurrentOrgAdmin && (
          <div className="current-admin-card">
            <div className="current-admin-card-content">
              <img
                src={effectiveCurrentOrgAdmin?.image || blank}
                alt=""
                className="current-admin-avatar"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = blank;
                }}
              />
              <div className="current-admin-card-info">
                <span className="current-admin-card-label">Current org admin</span>
                <span className="current-admin-card-name">
                  {effectiveCurrentOrgAdmin?.username || effectiveCurrentOrgAdmin?.email || "No Name"}
                </span>
                {effectiveCurrentOrgAdmin?.email && (
                  <span className="current-admin-email">{effectiveCurrentOrgAdmin.email}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {selectedUser && selectedOrg && (
          <div className="selected-user-card">
            <div className="selected-user-card-content">
              <img
                src={selectedUser?.image || blank}
                alt=""
                className="selected-user-avatar"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = blank;
                }}
              />
              <div className="selected-user-card-info">
                <span className="selected-user-card-label">Selected user</span>
                <span className="selected-user-card-name">
                  {selectedUser?.username || "No Name"}
                </span>
                <div className="selected-user-login-details">
                  <div className="selected-user-detail-row">
                    <span className="detail-label">Login email:</span>
                    <span className="detail-value">{selectedUser?.email || "—"}</span>
                  </div>
                  {/* <div className="selected-user-detail-row">
                    <span className="detail-label">Password:</span>
                    <span className="detail-value password-masked">••••••••</span>
                  </div> */}
                </div>
                <div className="selected-user-actions">
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<LockResetIcon />}
                    onClick={handleResetPasswordOpen}
                  >
                    Reset password
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={handleUpdateEmailOpen}
                  >
                    Update login email
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<ArchiveIcon />}
                    onClick={handleArchiveUserClick}
                  >
                    {selectedUser?.status === "active" ? "Archive user" : "Activate user"}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<AdminPanelSettingsIcon />}
                    onClick={handleMakeOrgAdminClick}
                  >
                    Make org admin
                  </Button>
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant="outlined"
              color="error"
              size="small"
              className="selected-org-deselect-btn"
              onClick={handleDeselectUser}
              startIcon={<CloseIcon />}
            >
              Clear selection
            </Button>
          </div>
        )}

        {!selectedUser && (
          <div className="internal-wrapper" ref={userRef}>
            <div className={`org-search-wrap ${userSearchQuery ? "has-clear" : ""}`}>
              <input
                type="text"
                placeholder="Search User..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                onClick={handleUserInputClick}
                disabled={!selectedOrg}
                className={userLoading ? "org-search-loading" : ""}
              />
              {userSearchQuery && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={clearUserSearch}
                  aria-label="Clear search"
                >
                  <CloseIcon fontSize="small" />
                </button>
              )}
            </div>

            <div className="organization-list">
              {!selectedOrg ? (
                <div className="orgListAlert">
                  Select an organization to view employees
                </div>
              ) : userLoading && !userList?.length ? (
                <div className="orgListAlert org-list-loading">
                  Loading...
                </div>
              ) : userNoResultsMessage ? (
                <div className="orgListAlert">No Such User Found</div>
              ) : userFilterEmpty ? (
                <div className="orgListAlert">
                  No employees match &quot;{userSearchQuery.trim()}&quot;. 
                </div>
              ) : !hasUserResults ? (
                <div className="orgListAlert">No employees found</div>
              ) : (
                displayUserList?.map((curr, index) => (
                  <div
                    className={`data-list ${curr?.id === selectedUser?.id ? "data-list-selected" : ""}`}
                    key={curr?.id ?? index}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelectUser(curr)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelectUser(curr);
                      }
                    }}
                  >
                    <div>
                      <img
                        src={curr?.image || blank}
                        alt="Logo"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = blank;
                        }}
                      />
                      <span>
                        {curr?.username ? curr?.username : "No Name"}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {selectedUser && selectedOrg && (
          <p className="select-user-hint">
            To choose a different user, click &quot;Clear selection&quot; above.
          </p>
        )}
      </div>

      <Dialog
        open={updateEmailOpen}
        onClose={handleUpdateEmailClose}
        slotProps={{
          paper: {
            sx: {
              minWidth: 380,
              borderRadius: 2,
              paddingBottom: 1,
            },
          }
        }}
      >
        <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>Update login email</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          <p className="update-email-dialog-hint">
            Enter the new email address for <strong>{selectedUser?.username ?? "this user"}</strong>. They will use it to sign in.
          </p>
          <TextField
            autoFocus
            label="New login email"
            type="email"
            fullWidth
            variant="outlined"
            value={updateEmailValue}
            onChange={(e) => setUpdateEmailValue(e.target.value)}
            placeholder={selectedUser?.email ?? "e.g. user@example.com"}
            sx={{
              marginTop: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button onClick={handleUpdateEmailClose}>Cancel</Button>
          <Button
            onClick={handleUpdateEmailSubmit}
            variant="contained"
            disabled={updateEmailLoading}
          >
            {updateEmailLoading ? "Updating…" : "Update email"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetPasswordOpen} onClose={handleResetPasswordClose}>
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New password"
            type={showResetPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={resetPasswordValue}
            onChange={(e) => setResetPasswordValue(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showResetPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowResetPassword((p) => !p)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showResetPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }}
          />
          <TextField
            margin="dense"
            label="Confirm password"
            type={showResetPasswordConfirm ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={resetPasswordConfirm}
            onChange={(e) => setResetPasswordConfirm(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showResetPasswordConfirm ? "Hide password" : "Show password"}
                      onClick={() => setShowResetPasswordConfirm((p) => !p)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showResetPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={resetPasswordNotify}
                onChange={(e) => setResetPasswordNotify(e.target.checked)}
                color="primary"
              />
            }
            label="Notify user (send email notification)"
            sx={{ marginTop: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetPasswordClose}>Cancel</Button>
          <Button
            onClick={handleResetPasswordSubmit}
            variant="contained"
            disabled={resetPasswordLoading}
          >
            {resetPasswordLoading ? "Resetting…" : "Reset password"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={archiveConfirmOpen} onClose={handleArchiveConfirmClose}>
        <DialogTitle>
          {selectedUser?.status === "active" ? "Archive user" : "Activate user"}
        </DialogTitle>
        <DialogContent>
          <p>
            {selectedUser?.status === "active"
              ? `Are you sure you want to archive "${selectedUser?.username || "this user"}"? They will be marked as inactive.`
              : `Are you sure you want to activate "${selectedUser?.username || "this user"}"? They will be able to access the account again.`}
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveConfirmClose}>Cancel</Button>
          <Button onClick={handleArchiveUserConfirm} variant="contained" color="warning">
            {selectedUser?.status === "active" ? "Archive" : "Activate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={makeOrgAdminOpen} onClose={handleMakeOrgAdminClose} maxWidth="sm" fullWidth>
        <DialogTitle>Make org admin</DialogTitle>
        <DialogContent>
          {effectiveCurrentOrgAdmin ? (
            <>
              <p className="make-org-admin-intro">
                Update the current admin&apos;s location and category so they remain under a location and category, then set the selected user as the new org admin.
              </p>
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel id="admin-location-label">Current admin – Location</InputLabel>
                <Select
                  labelId="admin-location-label"
                  value={adminLocationId}
                  label="Current admin – Location"
                  onChange={(e) => {
                    setAdminLocationId(e.target.value);
                    setAdminCategoryId("");
                  }}
                >
                  <MenuItem value="">
                    <em>Select location</em>
                  </MenuItem>
                  {(locationList || []).map((loc) => (
                    <MenuItem key={loc?.id} value={loc?.id}>
                      {loc?.location ?? loc?.name ?? loc?.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel id="admin-category-label">Current admin – Category</InputLabel>
                <Select
                  labelId="admin-category-label"
                  value={adminCategoryId}
                  label="Current admin – Category"
                  onChange={(e) => setAdminCategoryId(e.target.value)}
                  disabled={!adminLocationId}
                >
                  <MenuItem value="">
                    <em>Select category</em>
                  </MenuItem>
                  {(categoryList || []).map((cat) => (
                    <MenuItem key={cat?.id} value={cat?.id}>
                      {cat?.category_id ?? cat?.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                margin="normal"
                size="small"
                label="Current admin – Designation"
                placeholder="e.g. Admin, Manager"
                variant="outlined"
                value={adminDesignation}
                onChange={(e) => setAdminDesignation(e.target.value)}
              />
            </>
          ) : (
            <p className="make-org-admin-intro">
              No current org admin. The selected user will be set as org admin.
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMakeOrgAdminClose}>Cancel</Button>
          <Button
            onClick={handleMakeOrgAdminSubmit}
            variant="contained"
            disabled={makeOrgAdminLoading || (!!effectiveCurrentOrgAdmin && (!adminLocationId || !adminCategoryId))}
          >
            {makeOrgAdminLoading
              ? "Updating…"
              : effectiveCurrentOrgAdmin
                ? "Update current admin & set new admin"
                : "Set as org admin"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrganizationList;
